import * as React from 'react';
 
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Tooltip } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { each } from 'lodash';
import moment from 'moment';
import Select, { components } from 'react-select';
import { DEFAULT_SELECTED_WEEK } from '../../stores/jotai/base';

const dateFormatter = 'MMM DD YYYY';
const instructorDateFormatter = 'M[/]D[/]YY';
const SHOW_ALL_OPTION = {
    value: DEFAULT_SELECTED_WEEK,
    label: "Live View",
};

interface IState {
    selectedWeek: any;
    isMenuOpen: boolean;
}

interface IProps {
    cohort: CohortState;
    selectedWeek?: string;
    isForCanvas?: boolean; // True if we should style it for the BMC
    canvasDateLabel?: string; // What label to render in the input field while on the canvas
    onWeekChange?: (week: string) => void;
    theme?: Theme;
}

interface Refs {
    [key: string]: React.Component<any, any>;
    periodDropdown: Select;
}



export class PeriodSelector extends React.Component<IProps, IState> {
    state: IState = {
        selectedWeek: SHOW_ALL_OPTION,
        isMenuOpen: false
    }

    refs: Refs;

    componentDidMount() {
        if (this.props.selectedWeek !== undefined) {
            this.setSelectionToProps(this.props);
        }

        ((typeof window !== "undefined") ? document : {}).addEventListener("keydown", this.handleEscapePress, false);
    }

    componentWillUnmount() {
        ((typeof window !== "undefined") ? document : {}).removeEventListener("keydown", this.handleEscapePress, false);
    }

    UNSAFE_componentWillUpdate(nextProps, nextState) {
        if (nextProps.selectedWeek !== this.props.selectedWeek) {
            this.setSelectionToProps(nextProps);
        }
    }

    handleEscapePress = (event) => {
        if (event.keyCode === 27) {
            this.setState({
                isMenuOpen: false
            });
        }
    }

    setSelectionToProps = (props) => {
        const availablePeriodItems = this.buildPeriodItems();

        each(availablePeriodItems, periodItem => {
            if (periodItem?.value === props?.selectedWeek) {
                this.setState({
                    selectedWeek: periodItem
                });
            }
        });
    }

    buildWeekTouple = () => {
        const {
            cohort
        } = this.props;

        if (cohort === undefined || cohort === null) {
            return {}
        }

        let weekSet = [];

        let mToday = moment().add(1, 'weeks').format(dateFormatter);

        mToday = moment().format(dateFormatter);

        let firstPeriod = undefined;
        let lastPeriod = undefined;

        each(cohort.periods, period => {
            if (firstPeriod === undefined) firstPeriod = period;
            weekSet.push(moment(period.endDate));

            lastPeriod = period;
        });

        if (firstPeriod === undefined) {
            return {}
        }

        return {
            mStart: moment(firstPeriod.endDate).format(dateFormatter),
            mEnd: moment(lastPeriod.endDate).format(dateFormatter),
            mToday,
            weekSet
        }
    }

    determineWeekTitleByIndex = (currentWeek, prevWeek, weekIndex) => {
        const {
            weekSet
        } = this.buildWeekTouple();

        let title = '';

        let beforeDate = "Beginning";

        if (weekSet.length === 0) return "";

        if (prevWeek === undefined) {
            if (currentWeek === undefined) return beforeDate;

            return beforeDate + " - " + currentWeek.format(dateFormatter);
        }
        else {
            if (currentWeek === undefined) {
                return beforeDate;
            }
        }

        if (weekIndex > 0) beforeDate = prevWeek.format(dateFormatter);

        const endDate = currentWeek.format(dateFormatter);

        title = beforeDate + " - " + endDate;

        return title;
    }

    toggleOpen = () => {
        this.setState({
            isMenuOpen: !this.state.isMenuOpen
        });
    }

    // Array of {key: string, value: string} for our period dropdown
    buildPeriodItems() {
        const {
            weekSet
        } = this.buildWeekTouple() || {};
        const mTodayPushed = moment().add(1, 'days');
        const sortedWeekSet = Array.isArray(weekSet) ? [...weekSet] : [];
        let selecteableItems = [SHOW_ALL_OPTION];
        let firstFutureDateInserted = false;

        sortedWeekSet.sort((a,b) => {
            if (moment(a).isBefore(b)) {
                return -1;
            }

            if (moment(a).isAfter(b)) {
                return 1;
            }

            return 0;
        })

        const filteredWeekSet = sortedWeekSet?.filter((period) => {
            if (firstFutureDateInserted) {
                return false;
            }
            
            if (moment(period).isBefore(mTodayPushed)) {
                return true;
            }

            firstFutureDateInserted = true;

            return true;
        });

        filteredWeekSet.forEach((period, index) => {
            const periodLabel = this.props?.cohort?.periodLabel;
            const currentWeek = period;
            const prevWeek = filteredWeekSet[index - 1];

            selecteableItems.push({
                label:
                    periodLabel +
                    " " +
                    (index + 1) +
                    " (" +
                    this.determineWeekTitleByIndex(
                        currentWeek,
                        prevWeek,
                        index
                    ) +
                    ")",
                value: index + 1,
            });
        });

        return selecteableItems;
    }

    calculateClassName = () => {
        const {
            isForCanvas
        } = this.props;

        if (isForCanvas === true) {
            return "react-select-dd react-select-bmc";
        }

        return "react-select-dd";
    }

    calculateMenuProvider = (provided, state) => {
        const { theme } = this.props;
        return {
            ...provided,
            width: '310px',
            right: '0px',
            marginBottom: '0px',
            marginTop: 0,
            zIndex: theme?.custom?.menuZIndex,
        }
    }

    calculateControlProvider = (provided, state) => {
        const {
            isForCanvas, theme
        } = this.props;

        if (!isForCanvas) return {
            ...provided
        }

        return {
            ...provided,
            border: 'none',
            backgroundColor: 'transparency',
            outline: 'none',
            position: 'relative',
            boxShadow: 'none',
            paddingTop: '0px',
            marginLeft: '0px',
            paddingBottom: '0px',
            minHeight: 0,
            ':hover': {
                color: theme?.custom?.mainButtonHighlight,
            },
            cursor: 'pointer',
        }
    }

    calculateStyleProvider = (provided, state) => {
        const { theme } = this.props;
        return {
            ...provided,
            padding: "12px",
            color: state.isSelected ? "white" : "black",
            backgroundColor: state.isSelected
                ? theme?.custom?.mainButtonHighlight
                : "white",
            cursor: "pointer",
            "&:hover": {
                backgroundColor: state.isSelected
                    ? theme?.custom?.mainButtonHighlight
                    : theme?.custom?.hoverOptionColor,
                color: state.isSelected ? "white" : "black",
            },
        };
    }

    calculateValueContainerStyleProvider = (provided, state) => {
        return {
            padding: 0,
            minWidth: "90px",
            textAlign: "right",
        }
    }

    renderInput = (innerProps) => {
        return <span style={{
            cursor: 'pointer',
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        }}>{this.props?.canvasDateLabel}</span>
    }

    renderPlaceholder = (innerProps) => {
        return <span></span>
    }

    renderDropdownIndicator = (innerProps) => {
        return (
            <span
                style={{
                    cursor: "pointer",
                }}
            >
                <ExpandMoreIcon sx={{ color: "inherit" }} />
            </span>
        );
    }

    renderControl = (props) => {
        return (
            <Tooltip title="Canvas History" sx={{ }}>
                <span onClick={this.toggleOpen}>
                    <components.Control {...props} ></components.Control>
                </span>
            </Tooltip>
        )
    }

    render() {
        const {
            selectedWeek,
            isMenuOpen
        } = this.state;

        const {
            isForCanvas,
            cohort,
            theme
        } = this.props;

        // Don't render anything if the cohort isn't present
        if (cohort === undefined || cohort === null) {
            return <span>Current Date</span>
        }

        const styleProvider = {
            option: this.calculateStyleProvider,
            control: this.calculateControlProvider,
            menu: this.calculateMenuProvider,
            valueContainer: this.calculateValueContainerStyleProvider,
        };

        let componentOverrides = {}

        // Render certain components differently on the canvas
        if (isForCanvas === true) {
            componentOverrides = {
                Input: this.renderInput,
                Placeholder: this.renderPlaceholder,
                DropdownIndicator: this.renderDropdownIndicator,
                SingleValue: this.renderPlaceholder,
                IndicatorSeparator: this.renderPlaceholder,
                Control: this.renderControl,
            }
        }

        const clickAwayTracker = <span onClick={() => {
            this.setState({
                isMenuOpen: false
            });
        }} style={{ position: 'fixed', top: '0px', left: '0px', height: '100vh', width: '100vw', zIndex: theme?.custom?.menuClickAwayZIndex }} />

        return <span id="joy-discovery-period-selector" >
            {isMenuOpen ? clickAwayTracker : undefined}
            <Select
                menuIsOpen={isForCanvas ? isMenuOpen : undefined}
                components={componentOverrides}
                className={this.calculateClassName()}
                value={selectedWeek}
                styles={styleProvider}
                options={this.buildPeriodItems()}
                openMenuOnFocus={true}
                onChange={(options: any) => {
                    const newValue = options?.value;

                    this.setState({
                        selectedWeek: options
                    });

                    if (this.props.onWeekChange) {
                        this.props.onWeekChange(newValue);
                    }
                }}
            />
        </span>
    }
}
